import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { calcMaterials } from './MaterialCalculator';
/**
 * SimpleAreaCalculator
 * --------------------
 * A stripped-down area calculator used inline on the Product page. The
 * product context already tells us the MATERIAL — so we drop the
 * material / project-type pickers from the full MaterialCalculator and
 * just ask the shopper for shape + dimensions + depth.
 *
 * Live yardage updates as the user types. An "Apply" button hands the
 * computed yard count back to the parent (which then snaps the
 * quantity selector or splits multi-loads, exactly like the full
 * calculator did).
 */
type Shape = 'rect' | 'circle' | 'tri' | 'other';
type Props = {
  /** Material slug, used to look up density from calcMaterials. */
  materialSlug?: string;
  /** Fired when the shopper confirms — yard count is already rounded up. */
  onApply: (yards: number) => void;
};
export function SimpleAreaCalculator({
  materialSlug,
  onApply
}: Props) {
  const [shape, setShape] = useState<Shape>('rect');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('3');
  // Pull density from the shared materials registry so the tonnage /
  // yardage math matches the full calculator. Falls back to a 1.4
  // average if the slug isn't in the list.
  const density = useMemo(() => {
    const m = calcMaterials.find(x => x.id === materialSlug);
    return m ? m.density : 1.4;
  }, [materialSlug]);
  // Reset stale dimension state when the shape changes — keeps the
  // result from carrying over between shapes.
  useEffect(() => {
    setLength('');
    setWidth('');
    setDiameter('');
    setBase('');
    setHeight('');
  }, [shape]);
  const yards = useMemo(() => {
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return 0;
    const mToFt = 3.28084;
    const toFt = (v: string) => {
      const n = parseFloat(v) || 0;
      return unit === 'm' ? n * mToFt : n;
    };
    let areaSqFt = 0;
    if (shape === 'rect' || shape === 'other') {
      const l = toFt(length);
      const w = toFt(width);
      if (l <= 0 || w <= 0) return 0;
      areaSqFt = l * w;
    } else if (shape === 'circle') {
      const dia = toFt(diameter);
      if (dia <= 0) return 0;
      areaSqFt = Math.PI * (dia / 2) ** 2;
    } else if (shape === 'tri') {
      const b = toFt(base);
      const h = toFt(height);
      if (b <= 0 || h <= 0) return 0;
      areaSqFt = 0.5 * b * h;
    }
    const depthFt = depthUnit === 'cm' ? d / 30.48 : d / 12;
    const cubicYards = areaSqFt * depthFt / 27;
    return Math.ceil(cubicYards * 10) / 10;
  }, [shape, unit, depthUnit, length, width, diameter, base, height, depth]);
  const tons = Math.ceil(yards * density * 10) / 10;
  const canApply = yards > 0;
  const shapes: {
    id: Shape;
    label: string;
  }[] = [{
    id: 'rect',
    label: 'Rect'
  }, {
    id: 'circle',
    label: 'Circle'
  }, {
    id: 'tri',
    label: 'Tri'
  }, {
    id: 'other',
    label: 'Other'
  }];
  const inputCls = 'w-full px-4 py-2.5 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-2xl text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-[#04A05C]/35 focus:border-[#04A05C] outline-none transition-colors';
  const labelCls = 'block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1.5';
  return <div className="rounded-[28px] border border-stone-200 dark:border-[#4b4b4b] bg-white dark:bg-[#303030] p-5" data-id="element-1671" data-mp="zqbv14ljrzoq">
      <p className="text-xs font-bold tracking-widest text-stone-600 dark:text-stone-300 uppercase mb-4" data-id="element-1672" data-mp="uriibee34cpm">
        Area Shape
      </p>

      {/* Matches the calculator used in the materials hub: a clear selected
            shape, with the remaining choices kept as outlined buttons. */}
      <div className="grid grid-cols-4 gap-2 mb-5" data-id="element-1673" data-mp="2l3oo05og7eux">
        {shapes.map(({
        id,
        label
      }) => <button key={id} type="button" onClick={() => setShape(id)} className={`relative min-w-0 py-2.5 px-2 rounded-xl border text-sm font-bold transition-colors ${shape === id ? 'bg-[#04A05C] border-[#04A05C] text-white' : 'bg-white dark:bg-[#252525] border-stone-200 dark:border-[#404040] text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1674" data-mp="2q2rtfr8cbn3f">
            {label}
          </button>)}
      </div>

      {/* Compact rectangular segmented controls match the materials-hub
            calculator and keep the unit choices legible at mobile widths. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-5 text-xs" data-id="element-1675" data-mp="5b7w06ju8l9t">
        <div className="flex items-center gap-2" data-id="element-1676" data-mp="1pts73s9xusju">
          <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1677" data-mp="1psgy9e1myulv">
            Units:
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-stone-200 dark:border-[#404040]" data-id="element-1678" data-mp="1zkedqwzog8qe">
            {(['ft', 'm'] as const).map(u => <button key={u} type="button" onClick={() => setUnit(u)} className={`px-3 py-1.5 font-bold transition-colors ${unit === u ? 'bg-[#04A05C] text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1679" data-mp="1us3hc445b52l">
                {u}
              </button>)}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-1680" data-mp="o57svivroi5f">
          <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1681" data-mp="o5rf3bsg9sp9">
            Depth:
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-stone-200 dark:border-[#404040]" data-id="element-1682" data-mp="j7zc9mq9om6a">
            {(['in', 'cm'] as const).map(u => <button key={u} type="button" onClick={() => setDepthUnit(u)} className={`px-3 py-1.5 font-bold transition-colors ${depthUnit === u ? 'bg-[#04A05C] text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1683" data-mp="1cwx1fel6w9gv">
                {u}
              </button>)}
          </div>
        </div>
      </div>

      {/* Dimension inputs — change based on shape */}
      <AnimatePresence mode="wait" data-id="element-1684" data-mp="20mrg81p2k83r">
        {(shape === 'rect' || shape === 'other') && <motion.div key="rect" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-3 gap-3" data-id="element-1685" data-mp="25j818n0z26cm">
            <div data-id="element-1686" data-mp="3v1oiadjbyf4u">
              <label className={labelCls} data-id="element-1687" data-mp="3v155fb8jegxo">Length ({unit})</label>
              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1688" data-mp="3v415b53y3for" />
            </div>
            <div data-id="element-1689" data-mp="mnpkr7gpmlp8">
              <label className={labelCls} data-id="element-1690" data-mp="hn1vorc1kexn">Width ({unit})</label>
              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1691" data-mp="hnjyy7x4ftf2" />
            </div>
            <div data-id="element-1692" data-mp="15dxlxk0un22s">
              <label className={labelCls} data-id="element-1693" data-mp="15efkwoewbuee">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1694" data-mp="10kupn44oteiw" />
            </div>
          </motion.div>}
        {shape === 'circle' && <motion.div key="circ" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-2 gap-3" data-id="element-1695" data-mp="4w7ytpmbivw5">
            <div data-id="element-1696" data-mp="inmm48n9dj90">
              <label className={labelCls} data-id="element-1697" data-mp="iq8prx713ely">Diameter ({unit})</label>
              <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1698" data-mp="nleaisop2n8g" />
            </div>
            <div data-id="element-1699" data-mp="30s45z01xshhs">
              <label className={labelCls} data-id="element-1700" data-mp="30vs2m82ohk0q">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1701" data-mp="2vtsw4pbn66zk" />
            </div>
          </motion.div>}
        {shape === 'tri' && <motion.div key="tri" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-3 gap-3" data-id="element-1702" data-mp="2d4cvm1zyiej5">
            <div data-id="element-1703" data-mp="1ydm4yxa3plhm">
              <label className={labelCls} data-id="element-1704" data-mp="1yd3wn2a5zgtc">Base ({unit})</label>
              <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1705" data-mp="1tf2dkhcqnhhv" />
            </div>
            <div data-id="element-1706" data-mp="xoz3c9aev2ew">
              <label className={labelCls} data-id="element-1707" data-mp="xofk8p90y7za">Height ({unit})</label>
              <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1708" data-mp="toyf5wxifcun" />
            </div>
            <div data-id="element-1709" data-mp="eyzhrwds45g8">
              <label className={labelCls} data-id="element-1710" data-mp="f0ke9rjnlygq">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1711" data-mp="a0pltjk8d0tk" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Live result + apply CTA — appears only once we have a real number */}
      <AnimatePresence data-id="element-1712" data-mp="2hl4w0orw4qox">
        {canApply && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-1713" data-mp="2ho0t6avwzbcb">
            <div className="mt-5 pt-4 border-t border-[#e5e5e5] dark:border-[#4a4a4a] flex items-center justify-between gap-3" data-id="element-1714" data-mp="1xxf7qrj5x8kg">
              <div data-id="element-1715" data-mp="22vqhl29gjj2o">
                <p className="text-xs text-stone-500 dark:text-stone-400 uppercase font-bold tracking-wider" data-id="element-1716" data-mp="22vgt5j429jz3">
                  Estimate
                </p>
                <p className="text-lg font-extrabold text-stone-900 dark:text-white" data-id="element-1717" data-mp="1a7kw2dakxq1s">
                  {yards} yd
                  <span className="text-sm font-medium text-stone-500 dark:text-stone-400 ml-2" data-id="element-1718" data-mp="1f64nuubutw22">
                    ≈ {tons} tons
                  </span>
                </p>
              </div>
              <button type="button" onClick={() => onApply(yards)} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#04A05C] hover:bg-[#078d53] text-white text-sm font-bold rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#303030]" data-id="element-1719" data-mp="2dj0guas2g2ne">
                Use {Math.ceil(yards)} yd
                <ArrowRight className="w-4 h-4" data-id="element-1720" data-mp="28kym2sla2ew3" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}